import { Effect, Schema as S } from "effect";
import { defineMessageUnion } from "foldkit/message";
import * as Mount from "foldkit/mount";

import { i18next } from "../i18n";

/**
 * Profile image cropper (KTD6, AE10). Behavior contract derived from v1's
 * `ProfileImageUploader.tsx` (react-easy-crop):
 *
 * - Input: a JPEG or PNG `File` the page already validated (no GIF, ≤ 2 MB).
 * - Crop stage: a fixed 256×256 viewport; aspect ratio locked to 1:1, not
 *   user-adjustable; pan by drag; zoom by wheel/pinch only (no slider UI),
 *   using the library's default zoom range and step.
 * - Output: the selection drawn onto a 250×250 canvas and encoded as
 *   `image/jpeg` regardless of input type (PNG transparency is lost).
 * - Confirm ("Crop Image") resolves `CroppedImage({ blob })`; dismissing
 *   resolves `CropCancelled()`. Both arrive in `update` as Messages.
 *
 * Library: `cropperjs@1.6.3` (framework-agnostic, no React peer). The object
 * URL for the file is created in acquire and revoked in release, and the
 * Cropper instance is destroyed with it, so nothing outlives the element.
 */

const VIEWPORT = 256;
const OUTPUT = 250;

export const Message = defineMessageUnion({
	CroppedImage: { blob: S.instanceOf(Blob) },
	CropCancelled: {},
});
export type Message = typeof Message.Type;

const button = (label: string, className: string): HTMLButtonElement => {
	const el = document.createElement("button");
	el.type = "button";
	el.className = className;
	el.textContent = label;
	el.setAttribute("aria-label", label);
	return el;
};

export const CropImage = Mount.define("CropImage", {
	args: { file: S.instanceOf(File) },
	messages: [Message.CroppedImage, Message.CropCancelled],
	execute: ({ element, file }) =>
		Effect.gen(function* () {
			const stage = yield* Effect.acquireRelease(
				Effect.promise(async () => {
					const { default: Cropper } = await import("cropperjs");
					const url = URL.createObjectURL(file);
					// v1 `ProfileImageUploader`: `relative w-64 h-64 bg-base-200` crop stage.
					const frame = document.createElement("div");
					frame.className = "relative w-64 h-64 bg-base-200";
					frame.style.width = `${VIEWPORT}px`;
					frame.style.height = `${VIEWPORT}px`;
					const img = document.createElement("img");
					img.src = url;
					img.alt = i18next.t("cropper.imageAlt");
					img.style.maxWidth = "100%";
					frame.appendChild(img);
					const actions = document.createElement("div");
					actions.className = "mt-3 flex gap-3";
					const confirm = button(i18next.t("cropper.crop"), "btn btn-primary");
					const cancel = button(i18next.t("cropper.cancel"), "btn");
					actions.append(confirm, cancel);
					element.append(frame, actions);
					const cropper = new Cropper(img, {
						aspectRatio: 1,
						viewMode: 1,
						dragMode: "move",
						autoCropArea: 1,
						guides: false,
					});
					return { url, cropper, confirm, cancel, frame, actions };
				}),
				({ url, cropper, frame, actions }) =>
					Effect.sync(() => {
						cropper.destroy();
						URL.revokeObjectURL(url);
						frame.remove();
						actions.remove();
					}),
			);
			return yield* Effect.callback<Message>((resume) => {
				stage.cancel.onclick = () =>
					resume(Effect.succeed(Message.CropCancelled()));
				stage.confirm.onclick = () =>
					stage.cropper
						.getCroppedCanvas({ width: OUTPUT, height: OUTPUT })
						.toBlob(
							(blob) =>
								resume(
									Effect.succeed(
										blob
											? Message.CroppedImage({ blob })
											: Message.CropCancelled(),
									),
								),
							"image/jpeg",
						);
			});
		}),
});

/** Shows a blob in the host `<img>` for as long as it is mounted (the post-crop preview). */
export const PreviewBlob = Mount.define("PreviewBlob", {
	args: { blob: S.instanceOf(Blob) },
	messages: [S.Struct({ _tag: S.Literal("CompletedPreview") })],
	execute: ({ element, blob }) =>
		Effect.gen(function* () {
			yield* Effect.acquireRelease(
				Effect.sync(() => {
					const url = URL.createObjectURL(blob);
					(element as HTMLImageElement).src = url;
					return url;
				}),
				(url) => Effect.sync(() => URL.revokeObjectURL(url)),
			);
			return { _tag: "CompletedPreview" } as const;
		}),
});
